'use client';
import { Color, getCssVar } from '@web-libs/features/colors/application/colors';
import { CSSProperties, HTMLAttributeAnchorTarget, ReactNode } from 'react';
import ButtonStyles from './button.module.css';
import { TextSize } from '@web-libs/ui/ui-components/typo/types';
import { Text } from '@web-libs/ui/ui-components/typo/text';
import { LoadingIndicator } from '@web-libs/ui/ui-components/loading/loading-indicator';

export type ButtonColor =
  | 'primary'
  | 'dark'
  | 'light'
  | 'lightShade'
  | 'warn'
  | string;
export const textColorMap: { [key in ButtonColor]?: Color } = {
  primary: 'onPrimary',
  warn: 'onWarn',
  dark: 'light',
  light: 'dark',
  lightShade: 'dark',
};

export type ButtonProps = {
  text: string;
  color?: ButtonColor;
  onClick?: () => void;
  compact?: boolean;
  textSize?: TextSize;
  style?: CSSProperties;
  suffix?: ReactNode;
  inline?: boolean;
  disabled?: boolean;
  asAnchor?: boolean;
  href?: string;
  target?: HTMLAttributeAnchorTarget;
  loading?: boolean;
};
export const Button = ({
  text,
  color = 'primary',
  compact = true,
  onClick,
  style,
  suffix,
  textSize = 'sm',
  inline,
  disabled,
  asAnchor,
  href,
  target,
  loading,
}: ButtonProps) => {
  const colorVar = getCssVar(color);
  const textColorVar = getCssVar(textColorMap[color] ?? 'light');

  const calculatedStyles: CSSProperties = {
    backgroundColor: colorVar,
    color: textColorVar,
    opacity: disabled || loading ? 0.5 : 1,
    ...style,
  };

  const _onClick = () => {
    if (disabled || loading) {
      return;
    }
    onClick?.();
  };

  if (inline) {
    calculatedStyles.display = 'inline-block';
  }
  if (!suffix) {
    calculatedStyles.display = 'block';
  }

  const className = `${ButtonStyles.button} ${compact ? ButtonStyles.compact : ''}`;

  const buttonContent = loading ? (
    <LoadingIndicator color={textColorMap[color] ?? 'light'} small />
  ) : (
    <>
      <Text color={textColorMap[color] ?? 'light'} size={textSize}>
        {text}
      </Text>
      <div className={ButtonStyles.suffix}>{suffix}</div>
    </>
  );

  if (asAnchor) {
    return (
      <a
        className={className}
        onClick={_onClick}
        style={calculatedStyles}
        href={href}
        target={target}
      >
        {buttonContent}
      </a>
    );
  }

  return (
    <button
      disabled={disabled || loading}
      className={className}
      onClick={_onClick}
      style={calculatedStyles}
    >
      <div></div>
      {buttonContent}
    </button>
  );
};
